import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import BookingProposalPanel from "@/components/BookingProposalPanel";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ClipboardList, RefreshCw, AlertTriangle, CheckCircle2, Loader2,
  Video, CalendarClock, Sparkles, ChevronRight, Calendar, Clock,
} from "lucide-react";

export default function MyIntakePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, has_intake: false, intake: null, age_days: 0, needs_review: false });
  const [schema, setSchema] = useState([]);

  const load = async () => {
    try {
      const [mineRes, qRes] = await Promise.all([
        api.get("/intake/mine"),
        api.get("/intake/questions"),
      ]);
      setSchema(qRes.data?.questions || (Array.isArray(qRes.data) ? qRes.data : []));
      setState({ loading: false, ...(mineRes.data || { has_intake: false }) });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load intake");
      setState({ loading: false, has_intake: false });
    }
  };
  useEffect(() => { load(); }, []);

  const confirmNoChanges = async () => {
    try {
      await api.patch("/intake/mine", { confirm_no_changes: true });
      toast.success("Marked as up-to-date");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  if (state.loading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 text-white/60 py-10">
          <Loader2 className="animate-spin" size={16} /> Loading your intake…
        </div>
      </AppShell>
    );
  }

  if (!state.has_intake) {
    return (
      <AppShell>
        <div className="max-w-2xl">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00] mb-2">My intake</div>
          <h1 className="font-display text-4xl">You haven't submitted an intake yet.</h1>
          <p className="text-white/55 mt-3 max-w-lg">
            Once you book a session, your answers save here so Dr. Slava can prep before your call — and you'll never have to fill it out twice.
          </p>
          <Button
            onClick={() => navigate("/book-session")}
            data-testid="start-intake-btn"
            className="mt-6 rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]"
          >
            <ClipboardList size={14} className="mr-2" /> Start your intake
          </Button>
        </div>
      </AppShell>
    );
  }

  const intake = state.intake;
  const sections = [...new Set((schema || []).map((q) => q.section))]
    .filter((s) => (schema || []).some((q) => q.section === s && intake.answers && intake.answers[q.key] !== undefined));
  const updated = intake.updated_at || intake.created_at;

  return (
    <AppShell>
      <div className="max-w-3xl space-y-6" data-testid="my-intake-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00]">My intake</div>
            <h1 className="font-display text-4xl mt-1">Your session prep, at a glance.</h1>
            <p className="text-white/55 mt-2 text-sm max-w-xl">
              Everything Dr. Slava has on file. Skim this before your next call — and if anything's changed, tap Update.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">Last updated</div>
            <div className="font-display text-lg mt-0.5">
              {updated ? new Date(updated).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
            </div>
            <div className="text-xs text-white/40">{state.age_days} days ago</div>
          </div>
        </div>

        {/* Needs-review banner */}
        {state.needs_review && (
          <div className="glass rounded-2xl p-5 border border-[#FF8A00]/25 flex items-start gap-4" data-testid="needs-review-banner">
            <div className="w-10 h-10 rounded-lg bg-[#FF8A00]/15 text-[#FF8A00] flex items-center justify-center shrink-0">
              <AlertTriangle size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg">Time for a quick refresh</div>
              <p className="text-sm text-white/60 mt-1">
                It's been {state.age_days} days — a lot can shift in that window. Update anything that changed, or confirm you're the same.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  onClick={() => navigate("/book-session")}
                  data-testid="review-and-update-btn"
                  className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]"
                >
                  <RefreshCw size={13} className="mr-1.5" /> Review &amp; update
                </Button>
                <Button
                  onClick={confirmNoChanges}
                  variant="outline"
                  data-testid="confirm-no-changes-btn"
                  className="rounded-full bg-transparent border-white/15 hover:bg-white/5"
                >
                  <CheckCircle2 size={13} className="mr-1.5" /> Nothing's changed
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bilateral booking flow — propose / counter / accept */}
        {intake.status && intake.status !== "pending_payment" && (
          <BookingProposalPanel intake={intake} onRefresh={load} />
        )}

        {/* Upcoming session card (only shown after admin attaches Zoom on top of the confirmed panel) */}
        {false && intake.status === "confirmed" && intake.scheduled_at && intake.meeting_url && (
          <div className="glass rounded-2xl p-6 border border-emerald-500/25" data-testid="upcoming-session-card">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-11 h-11 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center">
                <Video size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.22em] uppercase text-emerald-300">Session confirmed</div>
                <div className="font-display text-xl mt-1">{intake.session_type_title}</div>
                <div className="text-sm text-white/60 mt-1 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1.5"><Calendar size={12}/> {new Date(intake.scheduled_at).toLocaleString()}</span>
                  <span className="text-white/30">·</span>
                  <span className="flex items-center gap-1.5"><Clock size={12}/> {intake.scheduled_duration_min || 60} min</span>
                </div>
              </div>
              {intake.meeting_url && (
                <Button asChild
                  data-testid="join-meeting-btn"
                  className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                  <a href={intake.meeting_url} target="_blank" rel="noreferrer">
                    <Video size={13} className="mr-1.5" /> Join the call
                  </a>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Not confirmed yet — pending_payment (proposal panel handles other states) */}
        {intake.status === "pending_payment" && (
          <div className="glass rounded-2xl p-5 flex items-start gap-3">
            <CalendarClock size={18} className="text-[#FF8A00] mt-1 shrink-0"/>
            <div>
              <div className="text-white/85">You're one step from booking — finish payment to lock in your slot.</div>
              <div className="text-xs text-white/45 mt-1">Session type: {intake.session_type_title}</div>
            </div>
          </div>
        )}

        {/* Session type & contact */}
        <div className="glass rounded-2xl p-6" data-testid="my-intake-summary">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00]">Session</div>
          <div className="font-display text-2xl mt-1">{intake.session_type_title}</div>
          <div className="text-sm text-white/55 mt-1">${(intake.price_cents / 100).toFixed(0)}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 text-sm">
            <Field label="Name" value={intake.name} />
            <Field label="Email" value={intake.email} />
            <Field label="Phone" value={intake.phone} />
          </div>
        </div>

        {/* Answers grouped by section */}
        {sections.map((s) => (
          <div key={s} className="glass rounded-2xl p-5">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00] mb-4">{s}</div>
            <div className="space-y-4">
              {(schema || []).filter((q) => q.section === s && intake.answers && intake.answers[q.key] !== undefined).map((q) => (
                <div key={q.key} className="border-t border-white/5 pt-3 first:border-t-0 first:pt-0">
                  <div className="text-sm text-white/55">{q.label}</div>
                  <div className="text-white/90 mt-1 whitespace-pre-wrap text-sm">
                    {formatValue(intake.answers[q.key])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Actions */}
        <div className="glass rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-lg">Something changed?</div>
            <div className="text-sm text-white/55 mt-0.5">Update your answers so Dr. Slava has the current picture.</div>
          </div>
          <Button
            onClick={() => navigate("/book-session")}
            data-testid="update-intake-btn"
            className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]"
          >
            <Sparkles size={13} className="mr-1.5" /> Book a session &amp; update <ChevronRight size={13} className="ml-1"/>
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">{label}</div>
      <div className="text-white/85 mt-0.5">{value || "—"}</div>
    </div>
  );
}

function formatValue(v) {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ") || "—";
  return String(v);
}
